import React, { useState, useRef } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { SpacingStack } from '@components/SpacingStack';
import { CloudImage } from '@components/CloudImage';
import { useAnswersCarouselController } from './AnswersCarousel.controller';
import {
  controlStyles,
  ImgWrapper,
  StyledCarousel,
  CarouselIndicatorWrap,
  MainTextPairing,
  AnswerWrapperBox,
} from './AnswersCarousel.styles';
import { useProximityFeedback } from '@hooks/useProximityFeedback';
import { CarouselIndicator } from '@components/Carousel';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { ActiveCarouselIndicatorDot, InactiveCarouselIndicatorDot } from '@components/Carousel/CarouselIndicator/CarouselIndicator.styles';
import { useTranslation } from 'react-i18next';

const nextSlideProximityParams = {
  distanceMin: 0,
  distanceMax: 500,
  feedbackValueFrom: 1,
  feedbackValueTo: 100,
};

export const AnswersCarousel: React.FC<{}> = () => {
  const ansCarouselId = 'Smart_Follow-up_Questions';
  const { ansCarouselRef } = useAnswersCarouselController();
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [shiftPosition, setShiftPosition] = useState(0);
  const [hoverSlide, setHoverSlide] = useState(0);
  let slideRefs = useRef<HTMLDivElement[]>([]);
  const isMobile = useThemeMediaQuery('below_sm');

  // Proximity effect values for left slides
  useProximityFeedback({ current: slideRefs.current[selectedSlide - 1] }, nextSlideProximityParams, (isInProximityArea, feedbackValue) => {
    if (isInProximityArea && feedbackValue > 20 && !isMobile) {
      setHoverSlide(selectedSlide);
      setShiftPosition(feedbackValue);
    }
  });
  // TODO: Figure out what this was doing here.
  // setShiftPosition;

  // Proximity effect values for right slides
  useProximityFeedback({ current: slideRefs.current[selectedSlide + 1] }, nextSlideProximityParams, (isInProximityArea, feedbackValue) => {
    if (isInProximityArea && feedbackValue > 20 && !isMobile) {
      setHoverSlide(selectedSlide + 2);
      setShiftPosition(-feedbackValue);
    }
  });

  const handleActiveSlide = (value: number) => {
    setSelectedSlide(value);
  };

  const inactiveComponent = () => {
    return <InactiveCarouselIndicatorDot />;
  };

  const activeComponent = () => {
    return <ActiveCarouselIndicatorDot />;
  };

  const { t } = useTranslation('onlineRsvp');
  const answers = t('answers');

  return (
    <AnswerWrapperBox backgroundColor="green1" paddingY={[4, 12]} ref={ansCarouselRef} id={ansCarouselId}>
      <SpacingStack spacing={11}>
        <MainTextPairing
          alignment="center"
          eyebrow={answers.eyebrow}
          eyebrowColor="green4"
          eyebrowVariant="sectionEyeBrow24"
          eyebrowTagName="h2"
          title={ReactHtmlParser(answers.title)}
          titleVariant="hed72Hero"
          titleColor="green6"
          titleTagName="h2"
        />
        <StyledCarousel
          shiftPosition={shiftPosition}
          hoverSlide={hoverSlide}
          selectedSlide={selectedSlide + 1}
          renderBottomCenterControls={null}
          slideIndex={selectedSlide}
          cellAlign={'center'}
          slidesToShow={1.5}
          beforeSlide={(curIndex, newIndex) => {
            return setSelectedSlide(newIndex);
          }}
          defaultControlsConfig={controlStyles}
          autoplay={false}
          initialSlideHeight={430}
          wrapAround={true}
        >
          {answers.slides.map((slide, index) => (
            <ImgWrapper ref={(el) => el && (slideRefs.current[index] = el)} id={`SliderId${index}`} key={index}>
              <CloudImage alt={slide} width="100%" src={slide} />
            </ImgWrapper>
          ))}
        </StyledCarousel>
      </SpacingStack>
      <CarouselIndicatorWrap>
        <CarouselIndicator
          quantity={answers.slides.length}
          dotsPerPage={answers.slides.length}
          infinite={false}
          activeIndex={selectedSlide}
          onDotSelected={handleActiveSlide}
          renderDot={inactiveComponent()}
          renderDotActive={activeComponent()}
        />
      </CarouselIndicatorWrap>
    </AnswerWrapperBox>
  );
};
